import { type ReactNode, useState } from 'react'
import { formatCallTime } from '@/entities/report'
import { cn, pluralRu, zoneDayHint, zoneTag } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'

// Строка среза глазами раздела: субъект, его цифры и — у сотрудника — разбивка,
// которая раскрывается на месте (данные приехали вместе со строкой).
export interface ReportItem {
  key: string
  title: string
  note?: string | undefined
  seconds: number
  meetingCount: number
  privateMeetingCount: number
  breakdown?: ReactNode
}

const SKELETON_ROWS = ['a', 'b', 'c', 'd']

export function ReportSkeleton() {
  return (
    <div>
      <div className="h-16 animate-pulse rounded-card bg-bg-elevated" />
      <div className="mt-6 flex flex-col gap-2">
        {SKELETON_ROWS.map((row) => (
          <div key={row} className="h-14 animate-pulse rounded-card bg-bg-elevated" />
        ))}
      </div>
    </div>
  )
}

function ReportError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-card bg-bg-surface py-14 text-center">
      <p className="text-text-secondary">Не удалось собрать отчёт</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        Повторить
      </Button>
    </div>
  )
}

// Крупный итог периода: сумма времени и с кого она собрана. Встречи по строкам
// не складываются — на общей встрече их бы посчитали по разу за участника.
function Summary({
  totalSeconds,
  subjects,
  subjectWord,
  periodLabel,
}: {
  totalSeconds: number
  subjects: number
  subjectWord: [string, string, string]
  periodLabel: string
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-border-subtle border-b pb-5">
      <div className="flex min-w-0 flex-col gap-1">
        <span className="label">Итого за период</span>
        <span className="text-sm text-text-tertiary">{periodLabel}</span>
        {/* Границы периода — календарные сутки зоны организации, а не момент:
            без этой строки цифры отчёта необъяснимы смотрящему из другой зоны
            (у него «вчера» кончается в другое время). */}
        <span className="text-text-tertiary text-xs" title={zoneDayHint()}>
          Сутки — по зоне {zoneTag()}
        </span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-4">
        <p className="t-h2 tabular-nums text-text-primary">Σ {formatCallTime(totalSeconds)}</p>
        <p className="text-sm text-text-tertiary">
          {subjects}&nbsp;{pluralRu(subjects, ...subjectWord)}
        </p>
      </div>
    </div>
  )
}

// Минибар доли — тихая подсветка распределения; точное значение стоит рядом
// цифрой, поэтому сам бар декоративен.
function ShareBar({ percent }: { percent: number }) {
  return (
    <span aria-hidden className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-pill bg-bg-sunken">
      <span
        className="block h-full rounded-pill bg-accent/40"
        style={{ width: `${Math.max(percent, 2)}%` }}
      />
    </span>
  )
}

// Приватные встречи дают числа, но не тему и не состав — строка честно говорит,
// сколько из её счётчика нераскрываемо.
function PrivateNote({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span className="inline-flex" title="Тема и состав приватных встреч не раскрываются">
      <Chip variant="soft">
        <Icon name="eye-off" size={12} />
        {count}
        {' '}
        {pluralRu(count, 'приватная', 'приватные', 'приватных')}
      </Chip>
    </span>
  )
}

function Row({ item, percent }: { item: ReportItem; percent: number }) {
  const [open, setOpen] = useState(false)
  const expandable = item.breakdown !== undefined

  const body = (
    <>
      <span className="flex min-w-0 flex-1 basis-48 items-center gap-2">
        {expandable && (
          <Icon
            name="chevron-down"
            size={16}
            className={cn('shrink-0 text-text-tertiary transition-transform', open && 'rotate-180')}
          />
        )}
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm text-text-primary">{item.title}</span>
          {item.note && <span className="truncate text-text-tertiary text-xs">{item.note}</span>}
        </span>
      </span>
      {/* На узком экране доля переносится вниз всей шириной. */}
      <span className="order-3 flex w-full items-center gap-3 sm:order-none sm:w-48">
        <ShareBar percent={percent} />
        <span className="w-9 text-right text-text-tertiary text-xs tabular-nums">{percent}%</span>
      </span>
      <span className="w-24 shrink-0 text-right text-sm text-text-secondary tabular-nums">
        {item.meetingCount}&nbsp;
        {pluralRu(item.meetingCount, 'встреча', 'встречи', 'встреч')}
      </span>
      <span className="w-28 shrink-0 text-right text-sm text-text-primary tabular-nums">
        {formatCallTime(item.seconds)}
      </span>
    </>
  )

  return (
    <li className="rounded-card bg-bg-surface">
      {expandable ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 rounded-card px-5 py-4 text-left transition-colors hover:bg-bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
        >
          {body}
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4">{body}</div>
      )}
      {(item.privateMeetingCount > 0 || (expandable && open)) && (
        <div className="border-border-subtle border-t px-5 py-3">
          <PrivateNote count={item.privateMeetingCount} />
          {expandable && open && item.breakdown}
        </div>
      )}
    </li>
  )
}

interface ReportViewProps {
  items: ReportItem[]
  subjectWord: [string, string, string]
  periodLabel: string
  isPending: boolean
  isError: boolean
  onRetry: () => void
  emptyTitle: string
}

// Срез за период: крупный итог сверху, строки с долями под ним. Состояния
// (загрузка / отказ / пусто) живут здесь же — страница остаётся оболочкой.
export function ReportView({
  items,
  subjectWord,
  periodLabel,
  isPending,
  isError,
  onRetry,
  emptyTitle,
}: ReportViewProps) {
  if (isPending) return <ReportSkeleton />
  if (isError) return <ReportError onRetry={onRetry} />
  if (items.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description="Попробуйте расширить период — пресеты «Прошлый месяц» и «90 дней» в выборе периода."
      />
    )
  }

  const totalSeconds = items.reduce((sum, item) => sum + item.seconds, 0)
  const share = (seconds: number) =>
    totalSeconds > 0 ? Math.round((seconds / totalSeconds) * 100) : 0

  return (
    <div>
      <Summary
        totalSeconds={totalSeconds}
        subjects={items.length}
        subjectWord={subjectWord}
        periodLabel={periodLabel}
      />
      <ul className="mt-6 flex flex-col gap-2">
        {items.map((item) => (
          <Row key={item.key} item={item} percent={share(item.seconds)} />
        ))}
      </ul>
    </div>
  )
}
